import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import toast from "react-hot-toast";
import {
    createTeamRole,
    deleteTeamRole,
    getTeamRoles,
    reorderTeamRoles,
    updateTeamRole,
} from "../../services/teams/getTeamRoles";
import { getRoleLabel } from "../../constants/roles";
import TeamRoleColorsModal from "./TeamRoleColorsModal";
import { isValidHexColor } from "./teamRoleColorValidation";
import { normalizeTeamRoles } from "./teamMembersUtils";
import { buildTeamRolePayload } from "./teamRolePayloads";

const emptyForm = {
    name: "",
    isTeamLead: false,
};

const getResponseMessage = (response, fallback) =>
    response?.status === 403
        ? "You don't have permission to do this."
        : response?.data?.message ?? response?.message ?? fallback;

const wasSuccessful = (response) =>
    response?.success === true && response?.data?.success !== false;

const getValidationDetails = (response) => {
    const payload = response?.data;
    const candidates = [
        payload?.affectedTeams,
        payload?.validationDetails,
        payload?.details,
        payload?.data?.affectedTeams,
        payload?.data?.validationDetails,
        payload?.data?.details,
    ];
    const details = candidates.find(Array.isArray) ?? [];

    return details
        .map((detail) => {
            if (typeof detail === "string") {
                return { teamName: "", issue: detail };
            }

            return {
                teamName: detail?.teamName ?? detail?.name ?? "",
                issue: detail?.issue ?? detail?.message ?? detail?.reason ?? "",
            };
        })
        .filter((detail) => detail.teamName || detail.issue);
};

const TeamRolesModal = ({
    open,
    setOpen,
    canRead,
    canWrite,
    canDelete,
    onRolesChanged,
}) => {
    const [teamRoles, setTeamRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [formMode, setFormMode] = useState(null);
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saveError, setSaveError] = useState(null);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [isColorsModalOpen, setIsColorsModalOpen] = useState(false);

    const loadRoles = async () => {
        if (!canRead) {
            setTeamRoles([]);
            return null;
        }

        setIsLoading(true);
        const response = await getTeamRoles();

        if (wasSuccessful(response)) {
            const responseRoles = response?.data?.teamRoles;
            const roles =
                Array.isArray(responseRoles) && responseRoles.length === 0
                    ? []
                    : normalizeTeamRoles(responseRoles);
            setTeamRoles(roles);
            setIsLoading(false);
            return roles;
        } else {
            setTeamRoles([]);
            toast.error(getResponseMessage(response, "Unable to load team roles."));
        }
        setIsLoading(false);
        return null;
    };

    useEffect(() => {
        if (!open) return;
        if (canRead) {
            loadRoles();
        } else {
            setTeamRoles([]);
            setIsLoading(false);
        }
        setFormMode(null);
        setEditingRoleId(null);
        setForm(emptyForm);
        setSaveError(null);
        setRoleToDelete(null);
    }, [canRead, open]);

    const openCreateForm = () => {
        if (!canWrite) return;
        setFormMode("create");
        setEditingRoleId(null);
        setForm(emptyForm);
        setSaveError(null);
    };

    const openEditForm = (role) => {
        if (!canWrite) return;
        setFormMode("edit");
        setEditingRoleId(role.id);
        setForm({
            name: role.name,
            isTeamLead: role.isTeamLead === true,
        });
        setSaveError(null);
    };

    const closeForm = () => {
        setFormMode(null);
        setEditingRoleId(null);
        setForm(emptyForm);
        setSaveError(null);
    };

    const saveRole = async (event) => {
        event.preventDefault();
        if (!canWrite || isSaving) return;

        const name = form.name.trim();

        if (!name) {
            toast.error("Role name is required.");
            return;
        }

        const editingRole = teamRoles.find((role) => role.id === editingRoleId);
        const payload = buildTeamRolePayload(
            { ...form, name },
            formMode === "edit" ? editingRole : null,
        );
        const isTeamLeadTransfer =
            formMode === "edit" &&
            editingRole?.isTeamLead !== true &&
            form.isTeamLead === true;

        setSaveError(null);
        setIsSaving(true);
        const response =
            formMode === "edit"
                ? await updateTeamRole(editingRoleId, payload)
                : await createTeamRole(payload);

        if (wasSuccessful(response)) {
            toast.success(
                getResponseMessage(
                    response,
                    formMode === "edit"
                        ? "Team role updated successfully."
                        : "Team role created successfully.",
                ),
            );
            closeForm();
            const roles = await loadRoles();
            await onRolesChanged?.(roles);
        } else {
            const message = getResponseMessage(
                response,
                "Unable to save team role.",
            );
            setSaveError({
                message,
                details: getValidationDetails(response),
                isTeamLeadTransfer,
            });
            toast.error(message);
        }
        setIsSaving(false);
    };

    const handleDragEnd = async ({ source, destination }) => {
        if (
            !canWrite ||
            isReordering ||
            !destination ||
            source.index === destination.index
        ) {
            return;
        }

        const teamLeadRole = teamRoles.find((role) => role.isTeamLead === true);
        if (!teamLeadRole) {
            toast.error("The Team Lead role could not be found.");
            return;
        }

        const previousRoles = [...teamRoles];
        const reorderedNonLeadRoles = teamRoles.filter(
            (role) => role.isTeamLead !== true,
        );
        const [movedRole] = reorderedNonLeadRoles.splice(source.index, 1);
        reorderedNonLeadRoles.splice(destination.index, 0, movedRole);

        const rolesWithUpdatedOrder = [
            { ...teamLeadRole, sortOrder: 0 },
            ...reorderedNonLeadRoles.map((role, index) => ({
                ...role,
                sortOrder: index + 1,
            })),
        ];

        setTeamRoles(rolesWithUpdatedOrder);
        setIsReordering(true);

        const response = await reorderTeamRoles(
            rolesWithUpdatedOrder.map((role, index) => ({
                teamRoleId: role.id,
                sortOrder: index,
            })),
        );

        if (wasSuccessful(response)) {
            toast.success(
                getResponseMessage(
                    response,
                    "Team role order updated successfully.",
                ),
            );
            const roles = await loadRoles();
            await onRolesChanged?.(roles);
        } else {
            setTeamRoles(previousRoles);
            toast.error(
                getResponseMessage(response, "Unable to update team role order."),
            );
        }

        setIsReordering(false);
    };

    const confirmDelete = async () => {
        if (!canDelete || !roleToDelete || roleToDelete.isTeamLead) return;
        setIsSaving(true);
        const response = await deleteTeamRole(roleToDelete.id);

        if (wasSuccessful(response)) {
            toast.success(
                getResponseMessage(response, "Team role deleted successfully."),
            );
            setRoleToDelete(null);
            const roles = await loadRoles();
            await onRolesChanged?.(roles);
        } else {
            toast.error(getResponseMessage(response, "Unable to delete team role."));
        }
        setIsSaving(false);
    };

    const openColorsModal = () => {
        if (!canWrite) return;
        setIsColorsModalOpen(true);
        setOpen(false);
    };

    const setColorsModalOpen = (nextOpen) => {
        setIsColorsModalOpen(nextOpen);
        if (!nextOpen) setOpen(true);
    };

    const handleColorsSaved = async (refreshedRoles) => {
        setTeamRoles(refreshedRoles);
        await onRolesChanged?.(refreshedRoles);
    };

    const teamLeadRole = teamRoles.find((role) => role.isTeamLead === true);
    const draggableTeamRoles = teamRoles.filter((role) => role.isTeamLead !== true);
    const editingRole = teamRoles.find((role) => role.id === editingRoleId);
    const isPreparingTeamLeadTransfer =
        formMode === "edit" &&
        editingRole?.isTeamLead !== true &&
        form.isTeamLead === true;
    const showActions = canWrite || canDelete;
    const columnCount = 3 + (canWrite ? 1 : 0) + (showActions ? 1 : 0);

    const renderRoleColor = (role) => {
        const colorHex =
            typeof role.colorHex === "string" ? role.colorHex.trim() : "";
        const hasValidColor = isValidHexColor(colorHex);

        return (
            <div className="flex items-center gap-2 text-slate-600">
                <span
                    className="block w-5 h-5 border rounded-sm border-slate-200"
                    style={{
                        backgroundColor: hasValidColor ? colorHex : "#ffffff",
                    }}
                />
                <span>{colorHex || "No color"}</span>
            </div>
        );
    };

    const renderRoleActions = (role) => (
        <>
            {canWrite && (
                <button
                    type="button"
                    onClick={() => openEditForm(role)}
                    disabled={isSaving || isReordering}
                    className="px-3 py-1 mr-2 text-white bg-teal-500 rounded-sm hover:bg-teal-600 disabled:opacity-50"
                >
                    Edit
                </button>
            )}
            {canDelete && (
                <span
                    title={
                        role.isTeamLead
                            ? "The Team Lead role cannot be deleted."
                            : undefined
                    }
                >
                    <button
                        type="button"
                        onClick={() => setRoleToDelete(role)}
                        disabled={role.isTeamLead || isSaving || isReordering}
                        className="px-3 py-1 text-white bg-red-500 rounded-sm hover:bg-red-600 disabled:opacity-50"
                    >
                        Delete
                    </button>
                </span>
            )}
        </>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={() => !isSaving && !isReordering && setOpen(false)}
                className="relative z-50"
            >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 w-screen overflow-y-auto">
                <div className="flex items-center justify-center min-h-full p-4">
                    <Dialog.Panel className="w-full max-w-4xl p-6 overflow-hidden text-left bg-white rounded-lg shadow-xl">
                        <div className="flex items-center justify-between">
                            <Dialog.Title className="text-xl font-bold text-slate-800">
                                Manage Team Roles
                            </Dialog.Title>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                disabled={isSaving || isReordering}
                                className="text-xl text-slate-500 hover:text-slate-700 disabled:opacity-50"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        {!canRead ? (
                            <div className="p-4 mt-6 text-sm border rounded-sm border-slate-200 text-slate-600">
                                You don't have permission to view team roles.
                            </div>
                        ) : roleToDelete && canDelete ? (
                            <div className="mt-6">
                                <h2 className="text-lg font-semibold text-slate-800">
                                    Delete {getRoleLabel(roleToDelete.name)}?
                                </h2>
                                <p className="mt-2 text-sm text-slate-600">
                                    Roles assigned to team members cannot be deleted.
                                    If this role is in use, the backend will prevent
                                    the deletion.
                                </p>
                                <div className="flex justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setRoleToDelete(null)}
                                        disabled={isSaving}
                                        className="px-4 py-2 text-sm border rounded-sm border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={confirmDelete}
                                        disabled={isSaving}
                                        className="px-4 py-2 text-sm text-white bg-red-500 rounded-sm hover:bg-red-600 disabled:opacity-50"
                                    >
                                        {isSaving ? "Deleting..." : "Delete Role"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {canWrite && (
                                    <div className="flex justify-end gap-2 mt-5">
                                        <button
                                            type="button"
                                            onClick={openColorsModal}
                                            disabled={isSaving || isReordering}
                                            className="h-10 text-white bg-slate-600 btn hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Manage Colors
                                        </button>
                                        <button
                                            type="button"
                                            onClick={openCreateForm}
                                            disabled={isSaving || isReordering}
                                            className="h-10 text-white bg-teal-500 btn hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Add Role
                                        </button>
                                    </div>
                                )}

                                {canWrite && formMode && (
                                    <form
                                        onSubmit={saveRole}
                                        className="grid gap-4 p-4 mt-4 border rounded-sm border-slate-200 bg-slate-50 md:grid-cols-2"
                                    >
                                        <label className="text-sm font-medium text-slate-700">
                                            Name
                                            <input
                                                type="text"
                                                value={form.name}
                                                onChange={(event) => {
                                                    setSaveError(null);
                                                    setForm((current) => ({
                                                        ...current,
                                                        name: event.target.value,
                                                    }));
                                                }}
                                                disabled={isSaving}
                                                className="block w-full px-3 py-2 mt-1 border rounded-sm border-slate-300 focus:ring-0 focus:border-teal-500"
                                            />
                                        </label>
                                        <label className="flex items-center gap-2 text-sm font-medium mt-7 text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={form.isTeamLead}
                                                onChange={(event) => {
                                                    setSaveError(null);
                                                    setForm((current) => ({
                                                        ...current,
                                                        isTeamLead:
                                                            event.target.checked,
                                                    }));
                                                }}
                                                disabled={isSaving}
                                                className="text-teal-600 rounded-sm border-slate-300 focus:ring-0"
                                            />
                                            Is Team Lead
                                        </label>
                                        {isPreparingTeamLeadTransfer && (
                                            <div
                                                role="status"
                                                className="p-3 text-sm border rounded-sm md:col-span-2 border-amber-300 bg-amber-50 text-amber-800"
                                            >
                                                Changing the Team Lead role affects
                                                every existing team. Each team must
                                                already have exactly one member
                                                assigned to this role before the
                                                change can be completed.
                                            </div>
                                        )}
                                        {saveError && (
                                            <div
                                                role="alert"
                                                className="p-3 text-sm text-red-700 border border-red-300 rounded-sm md:col-span-2 bg-red-50"
                                            >
                                                <p className="font-semibold">
                                                    {saveError.message}
                                                </p>
                                                {saveError.details.length > 0 && (
                                                    <ul className="mt-2 space-y-1 list-disc list-inside">
                                                        {saveError.details.map(
                                                            (detail, index) => (
                                                                <li
                                                                    key={`${detail.teamName}-${detail.issue}-${index}`}
                                                                >
                                                                    {detail.teamName && (
                                                                        <span className="font-medium">
                                                                            {
                                                                                detail.teamName
                                                                            }
                                                                            :
                                                                        </span>
                                                                    )}{" "}
                                                                    {detail.issue}
                                                                </li>
                                                            ),
                                                        )}
                                                    </ul>
                                                )}
                                                {saveError.isTeamLeadTransfer && (
                                                    <p className="mt-2">
                                                        Edit the affected teams and
                                                        assign exactly one member to
                                                        this role, then try again.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex justify-end gap-3 md:col-span-2">
                                            <button
                                                type="button"
                                                onClick={closeForm}
                                                disabled={isSaving}
                                                className="px-4 py-2 text-sm border rounded-sm border-slate-300 text-slate-700 hover:bg-white disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSaving}
                                                className="px-4 py-2 text-sm text-white bg-teal-500 rounded-sm hover:bg-teal-600 disabled:opacity-50"
                                            >
                                                {isSaving
                                                    ? "Saving..."
                                                    : "Save Role"}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                <div className="flex items-center justify-between mt-5 text-xs text-slate-500">
                                    <span>
                                        {canWrite
                                            ? "The Team Lead is always first. Drag other roles by the handle to change their order."
                                            : "The Team Lead is always first."}
                                    </span>
                                    {isReordering && <span>Saving order...</span>}
                                </div>
                                <div className="mt-5 overflow-x-auto border rounded-sm border-slate-200">
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <table className="w-full text-sm text-left">
                                            <thead className="text-slate-700 bg-slate-50">
                                                <tr>
                                                    {canWrite && (
                                                        <th className="w-12 px-4 py-3">
                                                            <span className="sr-only">
                                                                Reorder
                                                            </span>
                                                        </th>
                                                    )}
                                                    <th className="px-4 py-3">
                                                        Name
                                                    </th>
                                                    <th className="px-4 py-3">
                                                        Team Lead
                                                    </th>
                                                    <th className="px-4 py-3">
                                                        Color
                                                    </th>
                                                    {/* <th className="px-4 py-3">Type</th> */}
                                                    {showActions && (
                                                        <th className="px-4 py-3">
                                                            Actions
                                                        </th>
                                                    )}
                                                </tr>
                                            </thead>
                                            {isLoading ? (
                                                <tbody>
                                                    <tr>
                                                        <td
                                                            colSpan={columnCount}
                                                            className="px-4 py-8 text-center text-slate-500"
                                                        >
                                                            Loading team roles...
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            ) : teamRoles.length === 0 ? (
                                                <tbody>
                                                    <tr>
                                                        <td
                                                            colSpan={columnCount}
                                                            className="px-4 py-8 text-center text-slate-500"
                                                        >
                                                            No team roles found.
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            ) : (
                                                <>
                                                    {teamLeadRole && (
                                                        <tbody>
                                                            <tr className="border-t border-slate-200 bg-slate-50">
                                                                {canWrite && <td className="px-4 py-3">
                                                                    <span
                                                                        role="img"
                                                                        aria-label="Team Lead is fixed in the first position"
                                                                        title="Team Lead is always first"
                                                                        className="inline-flex items-center justify-center w-8 h-8 rounded-sm text-slate-500"
                                                                    >
                                                                        <svg
                                                                            aria-hidden="true"
                                                                            className="w-5 h-5"
                                                                            viewBox="0 0 24 24"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Zm-7-2a2 2 0 1 1 4 0v2h-4V7Zm2 10a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" />
                                                                        </svg>
                                                                    </span>
                                                                </td>}
                                                                <td className="px-4 py-3 font-medium text-slate-800">
                                                                    {getRoleLabel(
                                                                        teamLeadRole.name,
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    Yes
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap">
                                                                    {renderRoleColor(
                                                                        teamLeadRole,
                                                                    )}
                                                                </td>
                                                                {/* <td className="px-4 py-3">
                                                                    {teamLeadRole.isDefault
                                                                        ? "Default"
                                                                        : "Custom"}
                                                                </td> */}
                                                                {showActions && <td className="px-4 py-3 whitespace-nowrap">
                                                                    {renderRoleActions(
                                                                        teamLeadRole,
                                                                    )}
                                                                </td>}
                                                            </tr>
                                                        </tbody>
                                                    )}
                                                    <Droppable droppableId="team-roles">
                                                        {(provided) => (
                                                            <tbody
                                                                ref={
                                                                    provided.innerRef
                                                                }
                                                                {...provided.droppableProps}
                                                            >
                                                                {draggableTeamRoles.map(
                                                                    (
                                                                        role,
                                                                        index,
                                                                    ) => (
                                                                        <Draggable
                                                                            key={
                                                                                role.id
                                                                            }
                                                                            draggableId={
                                                                                role.id
                                                                            }
                                                                            index={
                                                                                index
                                                                            }
                                                                            isDragDisabled={
                                                                                !canWrite ||
                                                                                isReordering
                                                                            }
                                                                        >
                                                                            {(
                                                                                dragProvided,
                                                                                snapshot,
                                                                            ) => (
                                                                                <tr
                                                                                    ref={
                                                                                        dragProvided.innerRef
                                                                                    }
                                                                                    {...dragProvided.draggableProps}
                                                                                    className={`border-t border-slate-200 ${
                                                                                        snapshot.isDragging
                                                                                            ? "bg-teal-50 shadow-lg"
                                                                                            : "bg-white"
                                                                                    }`}
                                                                                >
                                                                                    {canWrite && <td className="px-4 py-3">
                                                                                        <button
                                                                                            type="button"
                                                                                            aria-label={`Reorder ${getRoleLabel(role.name)}`}
                                                                                            disabled={
                                                                                                !canWrite ||
                                                                                                isReordering
                                                                                            }
                                                                                            className="inline-flex items-center justify-center w-8 h-8 rounded-sm text-slate-500 cursor-grab active:cursor-grabbing hover:bg-slate-100 focus:ring-2 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-40"
                                                                                            {...dragProvided.dragHandleProps}
                                                                                        >
                                                                                            <svg
                                                                                                aria-hidden="true"
                                                                                                className="w-5 h-5"
                                                                                                viewBox="0 0 24 24"
                                                                                                fill="currentColor"
                                                                                            >
                                                                                                <path d="M4 7h16v2H4zM4 11h16v2H4zM4 15h16v2H4z" />
                                                                                            </svg>
                                                                                        </button>
                                                                                    </td>}
                                                                                    <td className="px-4 py-3 font-medium text-slate-800">
                                                                                        {getRoleLabel(
                                                                                            role.name,
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="px-4 py-3">
                                                                                        {role.isTeamLead
                                                                                            ? "Yes"
                                                                                            : "No"}
                                                                                    </td>
                                                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                                                        {renderRoleColor(
                                                                                            role,
                                                                                        )}
                                                                                    </td>
                                                                                    {/* <td className="px-4 py-3">
                                                                                        {role.isDefault
                                                                                            ? "Default"
                                                                                            : "Custom"}
                                                                                    </td> */}
                                                                                    {showActions && <td className="px-4 py-3 whitespace-nowrap">
                                                                                        {renderRoleActions(
                                                                                            role,
                                                                                        )}
                                                                                    </td>}
                                                                                </tr>
                                                                            )}
                                                                        </Draggable>
                                                                    ),
                                                                )}
                                                                {
                                                                    provided.placeholder
                                                                }
                                                            </tbody>
                                                        )}
                                                    </Droppable>
                                                </>
                                            )}
                                        </table>
                                    </DragDropContext>
                                </div>
                            </>
                        )}
                    </Dialog.Panel>
                </div>
            </div>
            </Dialog>
            <TeamRoleColorsModal
                open={isColorsModalOpen}
                setOpen={setColorsModalOpen}
                teamRoles={teamRoles}
                canWrite={canWrite}
                onSaved={handleColorsSaved}
            />
        </>
    );
};

export default TeamRolesModal;
